import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, CheckCircle } from 'lucide-react';
import {
  Button,
  Input,
  Field,
  FieldLabel,
  FieldError,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui';
import { useAuthStore } from '@/store/auth';
import { academyService } from '@/services/academy';
import { academyProfileService } from '@/services/academyProfile';
import type { Academy } from '@court-sync/shared';
import type { PublicOrganizationProfile } from '@/services/publicOrg';
import { useOrgPaths } from '@/hooks/useOrgPaths';
import { buildOrgEmailVerificationPath, buildOrgForgotPasswordPath, buildOrgRegisterPath, isClubMembershipReturnPath } from '@/lib/orgPaths';
import { getLandingContactUrl } from '@/lib/landingUrl';
import { isStarterMemberAuthBlockedAcademy } from '@/utils/publicBrowseMode';
import { BrowserImage } from '@/components/ui/BrowserImage';
import { useOrgRegisterDialogOptional } from '@/contexts/OrgRegisterDialogContext';
import type { OrgRegisterMode } from '@/contexts/OrgRegisterDialogContext';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface OrgLoginDialogProps {
  profile: PublicOrganizationProfile;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  returnUrl: string;
  message?: string;
  prefillEmail?: string;
}

export function OrgLoginDialog({
  profile,
  open,
  onOpenChange,
  returnUrl,
  message,
  prefillEmail,
}: OrgLoginDialogProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuthStore();
  const orgRegister = useOrgRegisterDialogOptional();
  const { p } = useOrgPaths(profile.organization);
  const orgSlug = profile.organization.slug;

  const [academy, setAcademy] = useState<Academy | null>(null);
  const [loadingAcademy, setLoadingAcademy] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open || !profile.academySlug) {
      setAcademy(null);
      return undefined;
    }
    let cancelled = false;
    setLoadingAcademy(true);
    academyService
      .getAcademyBySlug(profile.academySlug)
      .then((a) => {
        if (!cancelled) setAcademy(a);
      })
      .catch(() => {
        if (!cancelled) setAcademy(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingAcademy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, profile.academySlug]);

  const isOrgScopedReturnUrl = (url: string) => {
    if (!orgSlug || !p) return false;
    if (url.startsWith(`/club-academy/${orgSlug}`)) return true;
    const base = p('');
    if (url === base || url.startsWith(`${base}/`)) return true;
    return false;
  };

  const getRedirectUrl = (target: 'coach' | 'student' | 'member') => {
    if (returnUrl && isOrgScopedReturnUrl(returnUrl)) return returnUrl;
    if (target === 'coach' && profile.academySlug) {
      return `/academy/${profile.academySlug}/coach/dashboard`;
    }
    return p!('/dashboard');
  };

  const finishLogin = (targetPath: string) => {
    onOpenChange(false);
    const currentPath = `${location.pathname}${location.search}`;
    if (targetPath !== currentPath) {
      navigate(targetPath);
    }
  };

  const ensureAcademyProfileAndRedirect = async (user: {
    uid: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  }): Promise<boolean> => {
    const academySlug = profile.academySlug;
    if (!academySlug || !academy?.id) {
      finishLogin(getRedirectUrl('student'));
      return true;
    }
    try {
      const profileResult = await academyProfileService.createProfile({
        academySlug,
        firstName: user.firstName || 'Guardian',
        lastName: user.lastName || 'User',
        email: user.email,
      });
      if (profileResult.success) {
        finishLogin(getRedirectUrl('student'));
        return true;
      }
    } catch {
      // Profile might already exist
    }
    finishLogin(getRedirectUrl('student'));
    return true;
  };

  const handleUserRedirect = async (user: {
    uid: string;
    role?: string;
    roles?: string[];
    hasAcademyProfile?: boolean;
    firstName?: string;
    lastName?: string;
    email?: string;
  }) => {
    const hasGuardianAccess = !!(
      user?.roles?.includes('guardian') ||
      user?.roles?.includes('student') ||
      user?.role === 'guardian' ||
      user?.role === 'student' ||
      user?.hasAcademyProfile
    );
    const hasCoachAccess = !!user?.roles?.includes('coach');
    const hasMemberAccess = !!user?.roles?.includes('member');

    if (hasCoachAccess && !hasGuardianAccess && !hasMemberAccess && profile.academySlug) {
      finishLogin(getRedirectUrl('coach'));
      return true;
    }

    if (hasGuardianAccess && profile.modules.hasAcademy) {
      const bookingReturnUrl =
        returnUrl && (returnUrl.includes('/book-lesson/') || returnUrl.includes('/book/'))
          ? returnUrl
          : null;

      if (bookingReturnUrl && profile.academySlug) {
        try {
          await academyProfileService.createProfile({
            academySlug: profile.academySlug,
            firstName: user.firstName || 'Guardian',
            lastName: user.lastName || 'User',
            email: user.email,
          });
        } catch {
          // Profile may already exist
        }
        finishLogin(bookingReturnUrl);
        return true;
      }
      return await ensureAcademyProfileAndRedirect(user);
    }

    if (hasMemberAccess || profile.modules.hasClub) {
      finishLogin(getRedirectUrl('member'));
      return true;
    }

    if (returnUrl) {
      finishLogin(returnUrl);
      return true;
    }
    finishLogin('/dashboard');
    return true;
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: prefillEmail ?? '',
      password: '',
    },
  });

  useEffect(() => {
    if (open) {
      reset({ email: prefillEmail ?? '', password: '' });
    }
  }, [open, prefillEmail, reset]);

  const onSubmit = async (data: LoginFormData) => {
    try {
      setIsLoading(true);
      await login(data.email, data.password);
      const { user: loggedInUser } = useAuthStore.getState();
      if (loggedInUser) {
        if (!loggedInUser.isEmailVerified) {
          onOpenChange(false);
          const orgFields = {
            slug: profile.organization.slug,
            subdomain: profile.organization.subdomain,
            subdomainEnabled: profile.organization.subdomainEnabled,
          };
          const destination = returnUrl ?? (p ? p('/dashboard') : '/dashboard');
          navigate(
            buildOrgEmailVerificationPath(orgFields, {
              email: loggedInUser.email,
              returnUrl: destination,
              academySlug: profile.academySlug ?? undefined,
            })
          );
          return;
        }
        await handleUserRedirect(loggedInUser);
      } else {
        finishLogin(p ? p('/dashboard') : '/dashboard');
      }
    } catch (error: unknown) {
      const err = error as { message?: string };
      setError('root', { message: err.message || 'Invalid email or password' });
    } finally {
      setIsLoading(false);
    }
  };

  const org = profile.organization;
  const orgFields = { slug: org.slug, subdomain: org.subdomain, subdomainEnabled: org.subdomainEnabled };
  const defaultReturn = p?.('/dashboard') ?? '/dashboard';
  const forgotPasswordPath = buildOrgForgotPasswordPath(orgFields, returnUrl ?? defaultReturn);
  const registerPath = buildOrgRegisterPath(orgFields, returnUrl ?? defaultReturn);
  const registerMode: OrgRegisterMode =
    profile.modules.hasAcademy &&
    returnUrl &&
    !isClubMembershipReturnPath(returnUrl)
      ? 'academy'
      : 'club';
  const helpUrl = getLandingContactUrl();
  const portalSubtitle =
    profile.modules.hasAcademy && profile.modules.hasClub
      ? 'Sign in to manage lessons and membership'
      : profile.modules.hasClub
        ? 'Sign in to your membership portal'
        : 'Sign in to manage your bookings and schedule';

  const starterBlocked = Boolean(academy && isStarterMemberAuthBlockedAcademy(academy));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md gap-0 p-0 sm:max-w-md">
        <DialogHeader className="space-y-3 border-b border-border px-6 py-5 text-center">
          <div className="flex flex-col items-center gap-2">
            {org.logoUrl ? (
              <BrowserImage
                src={org.logoUrl}
                alt={org.name}
                className="h-12 w-auto max-w-[180px] object-contain"
                referrerPolicy="no-referrer"
              />
            ) : (
              <p className="text-base font-semibold text-foreground">{org.name}</p>
            )}
          </div>
          <div className="space-y-1">
            <DialogTitle>Sign in</DialogTitle>
            <DialogDescription>{portalSubtitle}</DialogDescription>
          </div>
        </DialogHeader>

        {loadingAcademy ? (
          <div className="px-6 py-8 text-sm text-muted-foreground">Loading…</div>
        ) : starterBlocked ? (
          <div className="space-y-4 px-6 py-6">
            <p className="text-sm text-muted-foreground">
              {academy?.name ? (
                <>
                  <span className="font-medium text-foreground">{academy.name}</span> uses the Starter
                  plan. Student and guardian accounts are not available — families can browse lessons and
                  submit a booking request instead.
                </>
              ) : (
                'Student and guardian sign-in is not available for this academy.'
              )}
            </p>
            {p ? (
              <Button asChild variant="outline" className="w-full">
                <Link to={p('/lessons')} onClick={() => onOpenChange(false)}>
                  Browse lessons
                </Link>
              </Button>
            ) : null}
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-0">
            <div className="space-y-4 px-6 py-5">
              {message ? (
                <div
                  className={`rounded-lg border p-4 ${
                    message.includes('verified')
                      ? 'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-200'
                      : 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200'
                  }`}
                >
                  <div className="flex items-center">
                    {message.includes('verified') ? (
                      <CheckCircle className="mr-2 h-5 w-5 shrink-0" />
                    ) : (
                      <AlertCircle className="mr-2 h-5 w-5 shrink-0" />
                    )}
                    <p className="text-sm font-medium">{message}</p>
                  </div>
                </div>
              ) : null}

              <Field data-invalid={!!errors.email?.message}>
                <FieldLabel htmlFor="org-login-email">Email address</FieldLabel>
                <Input
                  id="org-login-email"
                  type="email"
                  autoComplete="email"
                  {...register('email')}
                />
                <FieldError>{errors.email?.message}</FieldError>
              </Field>

              <Field data-invalid={!!errors.password?.message}>
                <div className="flex w-full items-center justify-between gap-2">
                  <FieldLabel htmlFor="org-login-password" className="mb-0 w-auto">
                    Password
                  </FieldLabel>
                  <Link
                    to={forgotPasswordPath}
                    className="shrink-0 text-xs font-medium text-primary hover:underline"
                    onClick={() => onOpenChange(false)}
                  >
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="org-login-password"
                  type="password"
                  autoComplete="current-password"
                  {...register('password')}
                />
                <FieldError>{errors.password?.message}</FieldError>
              </Field>

              {errors.root ? (
                <div className="rounded-md bg-destructive/10 p-3">
                  <p className="text-sm text-destructive">{errors.root.message}</p>
                </div>
              ) : null}
            </div>

            <DialogFooter className="flex-col gap-3 border-t border-border px-6 py-4 sm:flex-col sm:space-x-0">
              <Button type="submit" className="w-full" isLoading={isLoading} disabled={isLoading}>
                {isLoading ? 'Signing in...' : 'Sign in'}
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                Don&apos;t have an account?{' '}
                {orgRegister ? (
                  <button
                    type="button"
                    className="font-medium text-primary hover:underline"
                    onClick={() => {
                      onOpenChange(false);
                      orgRegister.openOrgRegister(returnUrl ?? defaultReturn, { mode: registerMode });
                    }}
                  >
                    Create account
                  </button>
                ) : (
                  <Link
                    to={registerPath}
                    className="font-medium text-primary hover:underline"
                    onClick={() => onOpenChange(false)}
                  >
                    Create account
                  </Link>
                )}
              </p>
              <p className="text-center text-sm text-muted-foreground">
                <a
                  href={helpUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-primary hover:underline"
                >
                  Need help?
                </a>
              </p>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
